/**
 * "Intro already seen" flag, per competition (specs/competitions-hub-client Slice 3D).
 *
 * R-7: for the World Cup this DELEGATES to the existing WC key, so every user who
 * already completed the WC welcome keeps their flag and can never see it again.
 * Other competitions get their own key, so seeing the WC intro doesn't silently
 * skip the season rules (and vice versa).
 */

import { platformStorage } from '../../shared/stores/platformStorage';
import { WORLD_CUP_COMPETITION_ID, WORLD_CUP_WELCOME_STORAGE } from '../../worldcup/worldcup.constants';
import { worldCupWelcomeStorageKey } from '../../worldcup/helpers/worldcupWelcomeStorageKey.helper';

const COMPETITION_INTRO_KEY_PREFIX = 'zporti.competitionIntro.v1';

/**
 * `contentId` is part of the key (specs/champions-league Story 8). `CompetitionIntroConfig`
 * always described it as "bump to re-show the intro after a content change", but nothing read
 * it, so a corrected rules screen would never have reached anyone who had already dismissed the
 * wrong one — the users who most need it. Omitting it keeps the pre-contentId key, so a caller
 * that has no content yet does not write a flag the real key will not find.
 */
export function competitionIntroStorageKey(
  competitionId: string,
  userId?: string | null,
  contentId?: string | null,
): string {
  if (competitionId === WORLD_CUP_COMPETITION_ID) {
    return worldCupWelcomeStorageKey(userId);
  }
  const suffix = userId?.trim() ? userId.trim() : 'anonymous';
  const base = `${COMPETITION_INTRO_KEY_PREFIX}.${competitionId}.${suffix}`;
  return contentId?.trim() ? `${base}.${contentId.trim()}` : base;
}

export async function readCompetitionIntroCompleted(
  competitionId: string,
  userId: string | null | undefined,
  contentId?: string | null,
): Promise<boolean> {
  const raw = await Promise.resolve(
    platformStorage.getItem(competitionIntroStorageKey(competitionId, userId, contentId)),
  );
  return raw === WORLD_CUP_WELCOME_STORAGE.COMPLETED_VALUE;
}

export async function writeCompetitionIntroCompleted(
  competitionId: string,
  userId: string | null | undefined,
  contentId?: string | null,
): Promise<void> {
  await Promise.resolve(
    platformStorage.setItem(
      competitionIntroStorageKey(competitionId, userId, contentId),
      WORLD_CUP_WELCOME_STORAGE.COMPLETED_VALUE,
    ),
  );
}
